import { CommonModule } from "@angular/common";
import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  AfterViewInit
} from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { FormlyModule } from "@ngx-formly/core";
import { MessageService } from "primeng/api";
import { ConfigService } from "../../core/services/config/config.service";
import { PieChartComponent } from "../../components/chart/pie-chart/pie-chart.component";
import { DynamicFormComponent } from "../../components/common/dynamic-form/dynamic-form.component";
import { DeleteConfirmationComponent } from "../../components/modal-templates/delete-confirmation/delete-confirmation.component";
import { LogsModalComponent } from "../../components/modal-templates/logs-modal/logs-modal.component";
import { SelfHealedObjectsModalComponent } from "../../components/modal-templates/self-healed-objects-modal/self-healed-objects-modal.component";
import { SharedComponents } from "../../components/shared.module";
import { CustomDateFormatPipe } from "../../core/pipes/custom-date-format.pipe";
import { MaterialModule } from "../../core/shared/material.module";
import { testExecutionColumn } from "../../core/table-templates/test-execution";
import { MessageEnum } from "../../core/utils/message.enum";
import { BaseLayoutComponent } from "../base-layout.component";

@Component({
  selector: "app-test-execution-results",
  standalone: true,
  imports: [
    CommonModule,
    CustomDateFormatPipe,
    ReactiveFormsModule,
    FormlyModule,
    ...SharedComponents,
    PieChartComponent,
    MaterialModule,
    DynamicFormComponent,
    LogsModalComponent
  ],
  templateUrl: "./test-execution-results.component.html",
  styleUrl: "./test-execution-results.component.scss",
})
export class TestExecutionResultsComponent
  extends BaseLayoutComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private modalService = inject(NgbModal);
  @ViewChild("menuTrigger") menuTrigger: any;

  private messageService = inject(MessageService);
  private configService = inject(ConfigService);
  latestExecution: any = {};
  rootStyles = getComputedStyle(document.documentElement);
  options: any =  { expandable: true };
  cardSummary: any[] = [];

  currentSummaryChart: any = {
    data: {},
    meta: {
      backgroundColors: [],
      legendPosition: "bottom",
      showLabels: false,
      cutout: "61%",
      isLegendVisible: false,
    },
  };

  overallSummaryChart: any = {
    data: {},
    meta: {
      backgroundColors: [],
      legendDirection: "right",
      showLabels: false,
      cutout: "65%",
      showPercentageInLegend: true,
    },
  };

  columns: any = testExecutionColumn.map((column: any) => ({ ...column }));
  isFilter: boolean = false;
  filterFields: any = [
    {
      name: "Execution Cycle",
      dataType: "list",
      type: "select",
      id: "cycle",
      key: "cycle",
      placeholder: "Execution Cycle",
      options: [],
    },
    {
      name: "Test Plan",
      dataType: "list",
      type: "select",
      id: "plan",
      key: "plan",
      placeholder: "Test Plan",
      options: [],
    },
    {
      name: "Status",
      dataType: "list",
      type: "select",
      id: "status",
      key: "status",
      placeholder: "Status",
      options: [],
    },
  ];
  filterForm: any = new FormGroup({});
  projectId: any;
  automationId: any = "";
  executionList: any = [];
  loading: boolean = false;
  intervalId: any;
  isIntervalLoading: boolean = false;
  selectedCycle: any;
  selectedPlan: any;
  selectedStatus: any;
  status: any = [];
  filteredCycles: any;
  filteredPlans: any;
  filteredStatus: any;
  tableData: any = [];
  meta: any;
  selectedExecutions:any = [];
  defaultMeta:any;

  ngOnInit(): void {
    this.projectId = this.dataStore?.data?.id;
    this.projectService.getAutomationProjectId().subscribe((item) => {
      this.automationId = item;
    });
    this.loadTestExecutionSummary(this.projectId, this.automationId);
  }

  ngAfterViewInit(): void {
    this.cardSummary = [
      {
        icon: `${this.basePath}assets/images/automation/pass.svg`,
        summaryType: "Passed",
        type: "passedCount",
        count: undefined,
      },
      {
        icon: `${this.basePath}assets/images/automation/fail.svg`,
        summaryType: "Failed",
        type: "failedCount",
        count: undefined,
      },
      {
        icon: `${this.basePath}assets/images/automation/executing.svg`,
        summaryType: "Executing",
        type: "executingCount",
        count: undefined,
      },
      {
        icon: `${this.basePath}assets/images/automation/waiting.svg`,
        summaryType: "Pending",
        type: "waitingCount",
        count: undefined,
      },
    ];
  }

  loadTestExecutionSummary(projectId: string, automationId: string, payload: any = {page: this.meta?.currentPage || 1, size: this.meta?.pageSize || 10}) {
    this.loading = true;
    this.restService.getTestExecutionSummary(projectId, automationId, payload).subscribe(
      (item) => {
        this.executionList = this.updateDateFields(item?.data);
        this.meta = item?.meta;
        this.latestExecution = this.executionList[0];
        this.loading = false;
        this.updateLastExecutionSummary();
        this.getOverallTestResultSummary(item?.summery);
        this.tableData = this.executionList;
        this.defaultMeta = this.meta;
        this.syncVmColumnVisibility(this.executionList);
        this.getCyclesList();
      },
      (error) => {
        this.loading = false;
      }
    );
  }

  getOverallTestResultSummary(summary: any) {
    const data = {
      passed: summary?.PASS,
      failed: summary?.FAIL,
      executing: summary?.EXECUTING,
      pending: summary?.PENDING,
    };
    const dataSet = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== 0)
    );
    this.overallSummaryChart.meta.backgroundColors = [];
    Object.keys(dataSet).forEach((key) => {
      this.overallSummaryChart.meta.backgroundColors.push(
        this.getColor(`${key}Count`)
      );
    });
    this.overallSummaryChart.data = dataSet;
  }

  formatDate = (dateString: any) => {
    const date = new Date(dateString);
    const options: any = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    };
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  // Function to recursively traverse the array and update date fields
  updateDateFields = (arr: any) => {
    arr.forEach((item: any) => {
      // Update startTime and endTime if they exist
      if (item?.startTime) {
        item.startTime = this.formatDate(item?.startTime);
      }
      if (item?.endTime) {
        item.endTime = this.formatDate(item?.endTime);
      }

      if (item?.automationTestPlanName) {
        item.name = item?.automationTestPlanName;
        item.isDownloadable = "true";
        item.isPlan = true;
      }

      if (item?.automationScriptName) {
        item.name = item?.automationScriptName;
        item.isDownloadable = "true";
        item.isTestCase = true;
      }

      if (item?.selfHealedObject?.length) {
        item.selfHealed = `${item?.selfHealedObject?.length} Self-healed Objects`;
      }

      // Recursively call updateDateFields if there are nested arrays
      if (Array.isArray(item?.children)) {
        this.updateDateFields(item?.children);
      }
    });
    return arr;
  };

  updateLastExecutionSummary() {
    let testCummaryChart: any = {};
    this.currentSummaryChart.meta.backgroundColors = [];
    this.cardSummary.forEach((item) => {
      item.count = this.latestExecution?.[item?.type];
      if (item.count) {
        testCummaryChart[item.type.replace(/Count$/, "")] = item.count;
        this.currentSummaryChart.meta.backgroundColors.push(
          this.getColor(item?.type)
        );
      }
    });
    this.currentSummaryChart.data = testCummaryChart;
  }

  getColor(type: string) {
    switch (type) {
      case "passedCount":
        return  this.rootStyles.getPropertyValue("--green").trim();
      case "failedCount":
        return  this.rootStyles.getPropertyValue("--red").trim();
      case "executingCount":
        return  this.rootStyles.getPropertyValue("--blue").trim();
      case "pendingCount":
        return  this.rootStyles.getPropertyValue("--magenta").trim();
      default:
        return  this.rootStyles.getPropertyValue("--grey1").trim();
    }
  }

  onRowHandle(params: any) {
    console.log(params);
    
    const actionType = this.getActionType(params);
    
    switch (actionType) {
      case 'download':
        this.handleDownloadReport(params);
        break;
      case 'open_report':
        this.handleOpenReport(params);
        break;
      case 'open_vm':
        this.handleOpenVm(params);
        break;
      case 'copy_id':
        this.handleCopyId(params);
        break;
      case 'link':
        this.handleSelfHealedObjects(params);
        break;
      case 'open_logs':
        this.handleOpenLogs(params);
        break;
    }
  }

  private getActionType(params: any): string | null {
    if (params?.type === "icon") {
      return params?.data?.columnConfig?.actionType;
    }
    if (params?.type === "copy_id" || params?.type === "link") {
      return params?.type;
    }
    return null;
  }

  private handleDownloadReport(params: any): void {
    const row = params?.data?.row;
    const reportType = row?.isPlan ? "PLAN" : "SCRIPT";
    
    this.restService
      .downloadExecutionReport(
        this.projectId,
        this.automationId,
        row?.id,
        reportType
      )
      .subscribe((item) => {
        window.open(item.data, "_blank");
      });
  }

  /**
   * The virtual machine column is shown only when the current records include
   * an agent machine URL. Otherwise the header and column are removed.
   */
  private syncVmColumnVisibility(records: any[]): void {
    const column = this.columns.find((item: any) => item.key === "openVm");
    if (!column) {
      return;
    }
    column.hidden = !this.hasAgentVmUrl(records);
  }

  private hasAgentVmUrl(records: any[]): boolean {
    if (!Array.isArray(records)) {
      return false;
    }
    return records.some((record) => this.recordHasAgentVmUrl(record));
  }

  private recordHasAgentVmUrl(record: any): boolean {
    const url = record?.agent?.configurations?.url;
    if (typeof url === "string" && url.trim().length > 0) {
      return true;
    }
    return this.hasAgentVmUrl(record?.children);
  }

  private handleOpenVm(params: any): void {
    const url = params?.data?.row?.agent?.configurations?.url;
    if (typeof url !== "string") {
      return;
    }
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return;
      }
      window.open(parsed.toString(), "_blank", "noopener,noreferrer");
    } catch {
      return;
    }
  }

  private handleOpenReport(params: any): void {
    const row = params?.data?.row;
    const reportUrl = this.configService.getConfig("reportUrl");
    
    if (!reportUrl) {
      console.error("Report URL is undefined");
      return;
    }

    const url = this.buildReportUrl(row);
    window.open(url, "_blank");
  }

  private buildReportUrl(row: any): string {
    const relem = this.utilityService?.extractRealmFromUrl() as string;
    const reportUrl = this.configService.getConfig("reportUrl");
    const tenantId = encodeURIComponent(relem);
    const projectId = encodeURIComponent(this.projectId);
    const automationProjectId = encodeURIComponent(this.automationId);
    const reportId = encodeURIComponent(row?.id ?? "");
    const type = row?.isPlan ? "PLAN" : "SCRIPT";

    return `${reportUrl}?tenantId=${tenantId}&projectId=${projectId}&automationProjectId=${automationProjectId}&reportId=${reportId}&type=${type}`;
  }

  private handleCopyId(params: any): void {
    this.copyToClipboard(params?.data?.row?.id);
  }

  private handleSelfHealedObjects(params: any): void {
    const modalRef = this.modalService.open(SelfHealedObjectsModalComponent, {
      centered: true,
      size: "xl",
      windowClass: "add-item-modal",
      backdrop: 'static',
    });
    
    modalRef.componentInstance.data = params?.data?.row?.selfHealedObject;
    modalRef.componentInstance.executionInstanceTaskId = params?.data?.row?.id;
    
    modalRef.result.then((result) => {
      console.log(result);
    });
  }

  private handleOpenLogs(params: any): void {
    const row = params?.data?.row;
    
    this.restService
      .getExecutionLogs(this.projectId, this.automationId, row?.id)
      .subscribe(
        (res) => this.openLogsModal(res?.data?.logs || ''),
        (error) => this.toaster.pop({ type: 'error', text: MessageEnum.FETCH_LOGS_FAILURE })
      );
  }

  private openLogsModal(logs: string): void {
    const configs = {
      title: "Logs",
      data: logs,
    };
    
    const modalRef = this.modalService.open(LogsModalComponent, {
      centered: true,
      size: "xl",
      backdrop: 'static',
    });
    
    modalRef.componentInstance.configs = configs;
    
    modalRef.result.then((result) => {
      console.log(result);
    });
  }

  @HostListener("document:click", ["$event"])
  clickOutside(event: Event) {
    if (this.menuTrigger && !this.eRef.nativeElement.contains(event.target)) {
      this.menuTrigger.closeMenu();
    }
  }

  // Close menu manually
  closeMenu() {
    this.menuTrigger.closeMenu();
  }

  // Detect clicks outside to close the menu
  onMenuClosed(event: any) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.menuTrigger.closeMenu();
    }
  }

  getStatus(status: string) {
    switch (status) {
      case "WAITING":
        return "waiting-class";
      case "ABORTED":
        return "aborted-class";
      case "EXECUTING":
        return "executing-class";
      case "PASS":
        return "passed-class";
      case "FAIL":
        return "failed-class";
      case "EXITED":
        return "exited-class"
      default:
        return "";
    }
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  getCyclesList() {
    const testCycles = this.executionList?.map((item: any) => ({
      label: item?.name,
      value: item?.id,
    }));
    this.filterFields[0].options = testCycles;
    this.filterForm?.controls["plan"].disable();
    this.filterForm?.controls["status"].disable();
    // this.filterForm.controls['cycle'].enable();
  }

  onTestCycleChange() {
    this.selectedCycle = this.tableData?.find(
      (cycle: any) => cycle?.id === this.filterForm?.value?.cycle
    );
    const testPlans = this.selectedCycle?.children?.map((item: any) => ({
      label: item?.automationTestPlanName,
      value: item?.automationTestPlanId ? item?.automationTestPlanId : item?.id,
    }));
    this.filterFields[1].options = testPlans;
    this.selectedPlan = null;
    this.selectedStatus = null;

    this.filterForm.controls["plan"].enable();
    this.filterForm.controls["status"].disable();
    this.filterForm.get('plan')?.setValue('');
    this.filterForm.get('status')?.setValue('');
  }

  onTestPlanChange() {
    this.selectedPlan = this.selectedCycle?.children?.find(
      (plan: any) =>
        plan?.automationTestPlanId === this.filterForm?.value?.plan ||
        plan?.id === this.filterForm?.value?.plan
    );

    // this.status = this.selectedPlan?.children?.map((item: any) => ({
    //   label: item?.status,
    //   value: item?.automationTestPlanId ? item?.automationTestPlanId : item?.id,
    // }));
    this.status = (this.selectedPlan?.children?.length
      ? this.selectedPlan.children
      : [this.selectedPlan]
    ).map((item: any) => ({
      label: item?.status,
      value: item?.automationTestPlanId ?? item?.id,
    }));

    this.filterFields[2].options = this.status;
    this.selectedStatus = null;
    this.filterForm.controls["status"].enable();
    this.filterForm.get('status')?.setValue('');
    this.selectedStatus = this.selectedPlan?.children[0];
  }

  onStatusChange() {
    this.selectedStatus = this.selectedPlan?.children?.find(
      (plan: any) =>
        plan?.automationTestPlanId === this.filterForm?.value?.status ||
        plan?.id === this.filterForm?.value?.status
    );
  }

  onModelChange(params: { field: any; value: string }) {
    switch (params?.field?.id?.toLowerCase()) {
      case "cycle":
        this.onTestCycleChange();
        break;

      case "plan":
        this.onTestPlanChange();
        break;

      case "status":
        this.onStatusChange();
        break;

      default:
        break;
    }
  }

  refresh() {
    this.loadTestExecutionSummary(this.projectId, this.automationId);
    this.reset();
  }

  reset() {
    this.executionList = this.tableData;
    this.syncVmColumnVisibility(this.executionList);
    this.meta = this.defaultMeta;
    this.selectedCycle = null;
    this.selectedPlan = null;
    this.selectedStatus = null;
    this.filterForm.controls["plan"].disable();
    this.filterForm.controls["status"].disable();
    this.filterForm.reset();
  }

  onClickFilter() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  filter() {
    this.executionList = this.filterTableData();
    this.syncVmColumnVisibility(this.executionList);
    if(this.executionList.length > 0){
      let customMeta = {
        currentPage: 1,
        hasNext : false,
        hasPrevious :false,
        pageSize:  10,
        totalItems: this.executionList.length,
        totalPages : Math.ceil(this.executionList.length / 10)
      }
      this.meta = customMeta;
    } else{
      this.executionList = this.tableData;
      this.syncVmColumnVisibility(this.executionList);
    }
    this.closeMenu();
  }

  filterTableData() {
    const cycleId = this.filterForm?.value?.cycle;
    const planId = this.filterForm?.value?.plan;
    const statusId = this.filterForm?.value?.status;
  
    // Filter cycles first
    this.filteredCycles = this.tableData?.filter((item: any) => item.id === cycleId) || [];
  
    if (planId && this.filteredCycles.length > 0) {
      const cycle = this.filteredCycles[0];
  
      this.filteredPlans = (cycle.children || []).filter(
        (item: any) => item.automationTestPlanId === planId || item.id === planId
      );
      cycle.children = this.filteredPlans;
      if (statusId && this.filteredPlans.length > 0) {
        const plan = this.filteredPlans[0];
        this.filteredStatus = (plan.children || []).filter(
          (item: any) => item.automationTestPlanId === statusId || item.id === statusId
        );

        plan.children = this.filteredStatus;
      }
    }

    return this.filteredCycles;
  }

  copyToClipboard(value: any): void {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        this.toaster.pop({ type: 'success', text: MessageEnum.TEST_CASE_ID_COPY_SUCCESS });
      })
      .catch((err) => {
        // alert('Failed to copy text: ' + err);
      });
  }

  onPageChange(event: any) {
    this.loadTestExecutionSummary(this.projectId, this.automationId, {page: event?.page, size: event?.size});
  }

  
  handleRowSelect(event: any) {
    this.selectedExecutions = event?.data?.filter((item: any) => item.select);
  }

  delete(){
    let modalRef = this.modalService.open(DeleteConfirmationComponent, {
          windowClass:"delete-confirmation-window",
          centered: true,
          size: "sm",
          backdrop: "static",
      });
      modalRef.result.then((result: any) => {
        if (result) {
          this.deleteExecutionReports();
        }
      });

  }

  deleteExecutionReports(){
    this.loading = true;
    const idList = this.selectedExecutions.map((item:any) => item?.id);
    
    this.restService.deleteExecutionsList(this.projectId,this.automationId,idList).subscribe({
      next: (response) => {
        this.loading = false;
        this.refresh();
        console.log("delete response->", response);
        this.toaster.pop({ type: 'success', text: MessageEnum.EXECUTIONS_DELETION_SUCCESS });
        this.selectedExecutions = [];
      },
      error: (err) => {
        this.loading = false;
        this.toaster.pop({ type: 'error', text: MessageEnum.EXECUTIONS_DELETION_FAILURE });
      },
    });
  }
}
